import { useState, useEffect } from 'react';

interface PWAInstallOptions {
  serviceKey?: string;
  onInstallSuccess?: () => void;
  onInstallError?: (error: Error) => void;
}

export const usePWAInstall = (options: PWAInstallOptions = {}) => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const checkInstallationStatus = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isIOSStandalone = (window.navigator as any).standalone === true;
      setIsInstalled(isStandalone || isIOSStandalone);
    };

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      options.onInstallSuccess?.();
    };

    checkInstallationStatus();
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [options]);
  const installPWA = async () => {
    if (!deferredPrompt) {
      throw new Error('PWA installation not available');
    }

    try {
      // Store the service key before installation
      if (options.serviceKey && options.serviceKey !== 'default') {
        localStorage.setItem('pwa_install_service_key', options.serviceKey);
        
        // Also store in session storage as backup
        sessionStorage.setItem('pwa_service_context', JSON.stringify({
          serviceKey: options.serviceKey,
          installTime: Date.now(),
          url: window.location.href,
          servicePath: `/${options.serviceKey}`
        }));
      }

      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user's response
      const { outcome } = await deferredPrompt.userChoice;      if (outcome === 'accepted') {
        console.log('PWA installation accepted for service:', options.serviceKey);
      } else {
        console.log('PWA installation declined');
      }

      // Clear the deferredPrompt
      setDeferredPrompt(null);
      setIsInstallable(false);

      return outcome === 'accepted';
    } catch (error) {
      options.onInstallError?.(error as Error);
      throw error;
    }
  };
  const getInstallInstructions = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.includes('iphone') || userAgent.includes('ipad')) {
      return {
        platform: 'iOS',
        instructions: [
          'Tap the Share button at the bottom of the screen',
          'Scroll down and tap "Add to Home Screen"',
          'Tap "Add" to confirm'
        ]
      };
    } else if (userAgent.includes('android')) {
      return {
        platform: 'Android',
        instructions: [
          'Tap the menu button (three dots) in your browser',
          'Select "Add to Home screen" or "Install app"',
          'Follow the prompts to install'
        ]
      };
    } else if (userAgent.includes('chrome')) {
      return {
        platform: 'Chrome',
        instructions: [
          'Look for the install icon in the address bar',
          'Click it and follow the prompts',
          'Or use menu → "Install [App Name]"'
        ]
      };
    } else {
      return {
        platform: 'Browser',
        instructions: [
          'Look for "Add to Home Screen" in your browser menu',
          'Or check for an install icon in the address bar',
          'Follow your browser\'s installation prompts'
        ]
      };
    }
  };

  return {
    isInstallable,
    isInstalled,
    installPWA,
    getInstallInstructions,
    canPromptInstall: !!deferredPrompt
  };
};